import React, { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Sheet from "components/sheet/Sheet";
import { ButtonNew as Button } from "components/buttonNew/index";
import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import Popup from "components/popup/Popup";
import { IProduct } from "redux/reducers/models/product";
import DataTable from "components/dataTable/DataTable";
import {
  CopyProductGroupProducts,
  DeleteProductGroup,
  DeleteProductGroupProduct,
  GetProductGroups,
  GetProductsInGroup,
  PostProductGroupProduct,
  PutProductGroupProduct,
  PutProductGroup,
} from "api/rpc/2024-04/facilityAdmin/product/groups";
import axios, { CancelToken } from "axios";
import { cloneDeep, isEqual } from "lodash";
import { isEmpty, valueToString } from "helpers/Helpers";
import MovableDataTableRow from "../../../../../components/dataTable/MovableDataTableRow";
import { IProductGroup, IProductInGroup } from "redux/reducers/models/groups";
import ProductSelect from "components/productModal/ProductSelect";

interface IProductGroupSaveState {
  title: string;
  addedProducts: IProductInGroup[];
}

interface IProductGroupState {
  id: number;
  prevTitle: string;
  deleteModalVisibility: boolean;
}

interface IProductGroupListProductGroup {
  id: number;
  title: string;
}

interface IProductGroupList {
  productGroups: IProductGroupListProductGroup[];
  selectedProductGroupID: number[];
}

interface IProductState {
  addProductVisibility: boolean;
  deleteProductVisibility: boolean;
  copyProductsVisibility: boolean;
  subgroups: IProductGroup[];
  groupHasSubGroups: boolean;
  selectedVariants: number[];
  selectedProducts: IProduct[];
}

const ProductGroup: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { productGroupId } = useParams<{ productGroupId: string }>();
  const dispatch = useAppDispatch();

  const [productGroupSaveState, setProductGroupSaveState] = useState<IProductGroupSaveState>({
    title: "",
    addedProducts: undefined,
  });

  const [productGroupSaveStateBeforeChanges, setProductGroupSaveStateBeforeChanges] =
    useState<IProductGroupSaveState>(null);

  const [productGroupListState, setProductGroupListState] = useState<IProductGroupList>({
    productGroups: [],
    selectedProductGroupID: [],
  });

  const [productGroupState, setProductGroupState] = useState<IProductGroupState>({
    id: undefined,
    prevTitle: "",
    deleteModalVisibility: false,
  });

  const [productState, setProductState] = useState<IProductState>({
    addProductVisibility: false,
    deleteProductVisibility: false,
    copyProductsVisibility: false,
    subgroups: undefined,
    groupHasSubGroups: false,
    selectedVariants: [],
    selectedProducts: [],
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadProductGroup(source.token);

    return () => {
      source.cancel();
    };
  }, []);

  async function loadProductGroups(token?: CancelToken) {
    const res = await GetProductGroups(null, token ? false : true, token);

    setProductGroupListState(prevState => ({
      ...prevState,
      productGroups: res.data.map(group => ({
        id: group.id,
        title: group.title,
      })),
    }));
  }

  async function handleDeleteProductGroup() {
    const deleteRes = await DeleteProductGroup(
      {
        id: productGroupState.id,
      },
      true,
    );

    if (deleteRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting product group")));
      return;
    }

    handleToggleDeleteModalVisibility();
    dispatch(showSuccess(t("Product group deleted successfully")));
    history.push("/admin/settings/groups");
  }

  async function copyProductsToProductGroup() {
    if (unsavedChangesExist()) {
      const result = await saveProductGroup();

      if (result === "failure") {
        return;
      }
    }

    const postRes = await CopyProductGroupProducts(
      {
        group_ids: productGroupListState.selectedProductGroupID,
        product_ids: productState.selectedVariants,
      },
      true,
    );
    if (postRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error copying products to product group")));
      return;
    }
    void handleCopyProductsVisibility();
    dispatch(showSuccess(t("Products successfully copied")));
  }

  type ProductGroupWithSubgroups = IProductGroup & { subgroups?: IProductGroup[] };

  async function loadProductGroup(token?: CancelToken, id?: string) {
    const newProductGroupId = isEmpty(id) ? productGroupId : id;

    const res = await GetProductGroups(null, token ? false : true, token);

    if (res.status !== StatusCode.OK) {
      return;
    }

    let selectedProductGroup: ProductGroupWithSubgroups;

    const groups: ProductGroupWithSubgroups[] = res.data;
    const subgroups: IProductGroup[] = [];

    groups.forEach(group => {
      if (Number(group.id) === Number(newProductGroupId)) {
        selectedProductGroup = group;
      }

      if (group.type === "group") {
        group.subgroups = [];
      }

      if (group.type === "sub_group") {
        subgroups.push(group);
      }
    });

    subgroups.forEach(subgroup => {
      groups.forEach(group => {
        if (subgroup.parent_id === group.id) {
          group.subgroups.push(subgroup);
        }
      });
    });

    const productsInGroupRes = await GetProductsInGroup(
      { group_id: Number(newProductGroupId) },
      token ? false : true,
      token,
    );

    productsInGroupRes.data?.sort((prev, next) => prev.position - next.position);

    setProductState(prevState => ({
      ...prevState,
      subgroups: selectedProductGroup?.subgroups,
      groupHasSubGroups: selectedProductGroup?.subgroups != null && selectedProductGroup.subgroups.length > 0,
    }));

    setProductGroupState(prevState => ({
      ...prevState,
      id: selectedProductGroup.id,
      prevTitle: selectedProductGroup.title,
    }));

    const updatedGroupSaveState: IProductGroupSaveState = {
      title: selectedProductGroup.title,
      addedProducts: productsInGroupRes.data,
    };

    setProductGroupSaveState(cloneDeep(updatedGroupSaveState));
    setProductGroupSaveStateBeforeChanges(cloneDeep(updatedGroupSaveState));
  }

  async function deleteProducts() {
    if (productState.selectedVariants.length <= 0) {
      dispatch(showError(t("A product to delete must be selected")));
      return;
    }

    if (unsavedChangesExist()) {
      const result = await saveProductGroup();

      if (result === "failure") {
        return;
      }
    }

    const res = await DeleteProductGroupProduct(
      {
        group_id: Number(productGroupId),
        product_ids: productState.selectedVariants,
      },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting product from group")));
      return;
    }

    setProductState(prevState => ({ ...prevState, selectedVariants: [] }));
    void loadProductGroup();
    void handleDeleteProductsVisibility();
  }

  async function saveProductGroup(): Promise<"success" | "failure"> {
    if (productGroupSaveState.addedProducts?.length > 0) {
      const putProductGroupProductRes = await PutProductGroupProduct(
        {
          group_id: parseInt(productGroupId),
          products: productGroupSaveState.addedProducts.map(product => ({
            product_id: product.id,
            position: product.position,
          })),
        },
        true,
      );

      if (putProductGroupProductRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error updating product group product positions")));
        return "failure";
      }
    }

    const editRes = await PutProductGroup(
      {
        id: Number(productGroupId),
        title: productGroupSaveState.title,
      },
      true,
    );

    if (editRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving product group")));
      return "failure";
    }

    setProductGroupState(prev => ({ ...prev, prevTitle: productGroupSaveState.title }));
    setProductGroupSaveStateBeforeChanges(cloneDeep(productGroupSaveState));

    return "success";
  }

  async function postProductGroupProduct(): Promise<"success" | "failure"> {
    const productIds = productState.selectedProducts?.map(product => product.id).filter(id => id != null) ?? [];

    if (productIds.length <= 0) {
      dispatch(showError(t("A product must be selected")));
      return "failure";
    }

    if (unsavedChangesExist()) {
      const result = await saveProductGroup();

      if (result === "failure") {
        return "failure";
      }
    }

    const postRes = await PostProductGroupProduct(
      {
        group_id: parseInt(productGroupId),
        product_ids: productIds,
      },
      true,
    );

    if (postRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding product to group")));
      return "failure";
    }

    return "success";
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setProductGroupSaveState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleToggleAllSelectedVariants() {
    if (productState?.selectedVariants?.length !== productGroupSaveState.addedProducts?.length) {
      setProductState(prev => ({
        ...prev,
        selectedVariants: productGroupSaveState.addedProducts.map(variant => variant.id),
      }));
    } else {
      setProductState(prev => ({ ...prev, selectedVariants: [] }));
    }
  }

  function handleSelectVariant(variant_id: number) {
    const foundId = productState.selectedVariants.find(val => val === variant_id);

    if (foundId != null) {
      setProductState(prev => ({
        ...prev,
        selectedVariants: prev.selectedVariants.filter(val => val !== variant_id),
      }));
    } else {
      setProductState(prev => ({ ...prev, selectedVariants: [...prev.selectedVariants, variant_id] }));
    }
  }

  function handleAddProductsVisibility() {
    setProductState(prevState => ({
      ...prevState,
      addProductVisibility: !prevState.addProductVisibility,
      selectedProducts: [],
    }));
  }

  function handleSelectProductGroupId(sheet_id: number) {
    const foundId = productGroupListState.selectedProductGroupID.find(val => val === sheet_id);

    if (foundId != null) {
      setProductGroupListState(prev => ({
        ...prev,
        selectedProductGroupID: prev.selectedProductGroupID.filter(val => val !== sheet_id),
      }));
    } else {
      setProductGroupListState(prev => ({
        ...prev,
        selectedProductGroupID: [...prev.selectedProductGroupID, sheet_id],
      }));
    }
  }

  function handleCopyProductsVisibility() {
    if (!productState.copyProductsVisibility && productGroupListState.productGroups.length <= 0) {
      void loadProductGroups();
    }

    setProductState(prevState => ({ ...prevState, copyProductsVisibility: !prevState.copyProductsVisibility }));
  }

  function handleToggleDeleteModalVisibility() {
    setProductGroupState(prevState => ({ ...prevState, deleteModalVisibility: !prevState.deleteModalVisibility }));
  }

  function handleDeleteProductsVisibility() {
    if (productState.selectedVariants.length <= 0) {
      dispatch(showError(t("Please select a variant to remove.")));
      return;
    }
    setProductState(prevState => ({ ...prevState, deleteProductVisibility: !prevState.deleteProductVisibility }));
  }

  async function handleSelectProducts() {
    const result = await postProductGroupProduct();

    if (result === "failure") {
      return;
    }

    setProductState(prevState => ({
      ...prevState,
      addProductVisibility: false,
      selectedProducts: [],
    }));
    void loadProductGroup();
  }

  function handleProductSelect(products: Array<IProduct>) {
    const selectedProducts = [...products];

    selectedProducts?.forEach((product, productIndex) => {
      product?.variants?.forEach((variant, variantIndex) => {
        if (variant?.quantity == null) {
          selectedProducts[productIndex].variants[variantIndex] = { ...variant, quantity: 1 };
        }
      });
    });

    setProductState(prevState => ({ ...prevState, selectedProducts: selectedProducts }));
  }

  const showDeleteAction = {
    content: t("Delete Category"),
    action: handleToggleDeleteModalVisibility,
  };

  function navigateToEditProductGroup(id: string) {
    history.push("/admin/settings/groups/" + id);
    void loadProductGroup(null, id);
    // window.location.href = url;
    // window.location.reload();
  }

  function unsavedChangesExist() {
    if (productGroupSaveStateBeforeChanges == null) {
      return false;
    }

    return !isEqual(productGroupSaveStateBeforeChanges, productGroupSaveState);
  }

  return (
    <>
      <Page
        title={isEmpty(productGroupState.prevTitle) ? " " : productGroupState.prevTitle}
        narrow
        secondaryActions={[showDeleteAction]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: saveProductGroup,
          onCancel: () => setProductGroupSaveState(cloneDeep(productGroupSaveStateBeforeChanges)),
        }}
        breadcrumbs={[
          {
            prefix: true,
            label: t("Categories"),
            url: "/admin/settings/groups",
          },
        ]}
      >
        <Card style={{ marginBottom: "50px" }}>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={productGroupSaveState.title}
                  label={t("Title")}
                  id="title"
                  onChange={handleInputChange}
                  placeholder={t("Product category title..")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
          <Card.Section>
            <div className="product-group-header">
              <div className="title-header">
                {productState.subgroups?.length > 0
                  ? t("Subcategories")
                  : t("Products")}
              </div>

              {!productState.groupHasSubGroups && (
                <div className="flex items-center">
                  <Button className="ml-2" type="secondary" size="xsmall" onClick={handleAddProductsVisibility}>
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />{" "}
                    <span>{t("Add")}</span>
                  </Button>

                  <Button className="ml-2" type="secondary" size="xsmall" onClick={handleDeleteProductsVisibility}>
                    <FontAwesomeIcon icon={["far", "trash-can"]} className="mr-2" />{" "}
                    <span>{t("Remove")}</span>
                  </Button>
                </div>
              )}
            </div>

            <DataTable
              enableDragAndDrop={!productState?.groupHasSubGroups}
              columns={
                !productState.groupHasSubGroups
                  ? [
                    {
                      label: "",
                      width: "7%",
                      content: (
                        <Checkbox
                          onChange={() => handleToggleAllSelectedVariants()}
                          size="small"
                          checked={
                            productGroupSaveState?.addedProducts?.length > 0 && 
                            productState?.selectedVariants?.length === productGroupSaveState.addedProducts?.length
                          }
                        />
                      ),
                    },
                    { label: t("Title"), width: "31%" },
                    { label: t("Subtitle"), width: "31%" },
                    { label: t("Type"), width: "31%" },
                  ]
                  : [
                    { label: t("Title"), width: "31%" },
                    { label: t("Type"), width: "31%" },
                  ]
              }
              loading={productGroupSaveState.addedProducts === undefined && productState.subgroups === undefined}
            >
              {!productState?.groupHasSubGroups ? (
                productGroupSaveState?.addedProducts?.length > 0 ? (
                  <Fragment>
                    {productGroupSaveState.addedProducts?.map(product => {
                      return (
                        <MovableDataTableRow
                          key={product.id}
                          id={product.id}
                          position={product.position}
                          className="clickable"
                          onClick={() => handleSelectVariant(product.id)}
                          rows={productGroupSaveState.addedProducts}
                          setRows={setAddedProductsState =>
                            setProductGroupSaveState(prev => ({
                              ...prev,
                              addedProducts: setAddedProductsState(prev.addedProducts),
                            }))
                          }
                        >
                          <td>
                            <Checkbox
                              size="small"
                              checked={productState?.selectedVariants?.includes(product.id)}
                              readOnly
                            />
                          </td>
                          <td>{product?.title}</td>
                          <td>{product?.subtitle}</td>
                          <td>{product?.type}</td>
                        </MovableDataTableRow>
                      );
                    })}
                  </Fragment>
                ) : (
                  <Fragment>
                    <tr>
                      <td colSpan={5} className="text-center">{t("No products in this category")}</td>
                    </tr>
                  </Fragment>
                )
              ) : (
                <Fragment>
                  {productState.subgroups?.map((subgroup, index) => {
                    return (
                      <tr
                        key={index}
                        className="clickable"
                        onClick={() => navigateToEditProductGroup(String(subgroup.id))}
                      >
                        <td>{subgroup?.title}</td>
                        <td>{valueToString(subgroup?.type)}</td>
                      </tr>
                    );
                  })}
                </Fragment>
              )}
            </DataTable>
          </Card.Section>
        </Card>
      </Page>

      {/* Add Products Sheet */}
      <Sheet
        open={productState.addProductVisibility}
        title={t("Add products")}
        size="medium"
        closable
        onCancel={handleAddProductsVisibility}
        cancelText={t("Close")}
        onOk={() => void handleSelectProducts()}
        okText={t("Add")}
        okDisabled={productState.selectedProducts.length === 0}
      >
        <ProductSelect
          selectedProducts={productState.selectedProducts}
          onChange={handleProductSelect}
          search
          hideVariants
          limit={50}
        />
      </Sheet>

      {/* Copy Products Sheet */}
      <Sheet
        closable
        title={t("Copy Products to Product Category")}
        size="medium"
        open={productState.copyProductsVisibility}
        cancelText={t("Close")}
        onCancel={handleCopyProductsVisibility}
        okText={t("Continue")}
        onOk={copyProductsToProductGroup}
      >
        <Card>
          <DataTable
            columns={[{ label: "" }, { label: t("Title") }]}
            loading={productGroupListState.productGroups === undefined}
          >
            {productGroupListState.productGroups?.map((group, index) => {
              return (
                <tr key={index} className="clickable" onClick={() => handleSelectProductGroupId(group.id)}>
                  <td>
                    <Checkbox
                      size="medium"
                      checked={productGroupListState?.selectedProductGroupID?.includes(group.id)}
                      readOnly
                    />
                  </td>
                  <td>{group.title}</td>
                </tr>
              );
            })}
          </DataTable>
        </Card>
      </Sheet>

      {/* Delete Product Group Warning */}
      <Popup
        open={productGroupState.deleteModalVisibility}
        type="warning"
        title={t("Delete Product Category")}
        description={t("Are you sure you want to delete the selected product category?")}
        onOk={handleDeleteProductGroup}
        okText={t("Delete")}
        onCancel={handleToggleDeleteModalVisibility}
        backDropCancel={true}
      />

      {/* Delete Product Group Product */}
      <Popup
        open={productState.deleteProductVisibility}
        type="warning"
        title={t("Remove Products")}
        description={t("Are you sure you want to remove the product category products?")}
        onOk={deleteProducts}
        okText={t("Delete")}
        onCancel={handleDeleteProductsVisibility}
        backDropCancel={true}
      />
    </>
  );
};

export default ProductGroup;
